import * as GlobalS from "@/components/styled";
import { DefaultImageSelector } from "@/containers/Project/components/Images/components";
import * as S from "@/containers/Project/components/styled";
import { ProjectUserPermissions } from "@/enums";
import { useProjectContext } from "@/providers";
import {
  useGetProjectImagesQuery,
  useGetSasMutation,
  useRemoveProjectImageMutation,
  useUploadFileMutation,
  useUploadProjectImageMutation,
} from "@/services";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Image, Spin, Upload } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";

const supportedImageTypes = ["image/jpeg", "image/png", "image/gif"];

export const ImagesCard = () => {
  const { projectId } = useParams();
  const intl = useIntl();
  const { canUser, project } = useProjectContext();
  const [imageList, setImageList] = useState([]);
  const [preview, setPreview] = useState({ active: false, index: 0 });
  const { data: projectImages, isFetching: imagesFetching } =
    useGetProjectImagesQuery({ projectId });
  const [uploadProjectImage] = useUploadProjectImageMutation();
  const [removeProjectImage] = useRemoveProjectImageMutation();
  const [getSas] = useGetSasMutation();
  const [uploadFile] = useUploadFileMutation();

  useEffect(() => {
    if (!projectImages) return;

    const images = projectImages.map((x) => ({
      ...x,
      uid: x.id,
      name: x.name,
    }));
    const imageIds = images.map((x) => x.id);
    setImageList((state) => {
      const otherImages = state.filter(
        (x) => x.status && !imageIds.includes(x.id)
      );
      return [...images, ...otherImages];
    });
  }, [projectImages]);

  const uploadItem = async ({ file, onProgress, onSuccess, onError }) => {
    const storageInfo = { name: file.name, id: v4() };
    getSas({ storageInfo, intakeId: projectId })
      .unwrap()
      .then((response) => {
        onProgress({ percent: 5, loaded: 0, total: file.size });
        return uploadFile({
          file,
          location: response.data.itemPath,
          onProgress: ({ percent, loaded, total }) => {
            const newProgress = {
              percent: (percent / 100) * 90 + 5,
              loaded: loaded,
              total: total,
            };
            onProgress(newProgress);
          },
        })
          .unwrap()
          .then((blobInfo) => {
            const uploadRequest = {
              id: storageInfo.id,
              itemName: file.name,
              contentType: file.type,
              blobListIds: blobInfo.blockIds,
            };

            return uploadProjectImage({
              uploadRequest,
              projectId,
            })
              .unwrap()
              .then(() => {
                onSuccess("success");
              })
              .catch(() => {
                onError("error");
                toast.error(intl.formatMessage({ id: "project.uploadError" }));
              });
          });
      });
  };

  const removeImage = (file) =>
    new Promise((resolve, reject) => {
      if (!file.id) {
        resolve();
        return;
      }

      removeProjectImage({ projectId: project.id, projectImageId: file.id })
        .unwrap()
        .then(() => {
          resolve();
        })
        .catch(() => {
          toast.error(intl.formatMessage({ id: "general.error" }));
          reject();
        });
    });

  const handleChange = ({ file, fileList }) => {
    setImageList((state) => {
      if (file.status === "done") {
        return state.filter((x) => x.uid !== file.uid);
      }
      return fileList;
    });
  };

  const handlePreview = (file) => {
    const index = imageList.findIndex((x) => x.uid === file.uid);
    setPreview({ show: true, index });
  };

  const validateImageType = (file, _fileList) => {
    const isSupported = supportedImageTypes.includes(file.type);
    if (!isSupported) {
      toast.error(
        intl.formatMessage(
          { id: "project.imageNotSupported" },
          { fileName: file.name }
        )
      );
    }
    return isSupported || Upload.LIST_IGNORE;
  };

  return (
    <GlobalS.GrayCard
      size="small"
      title={intl.formatMessage({ id: "project.images" })}
    >
      {!project || imagesFetching ? (
        <Spin />
      ) : (
        <>
          <S.StyledUpload
            accept="image/png, image/jpeg, image/gif"
            listType="picture-card"
            fileList={imageList}
            customRequest={uploadItem}
            onPreview={handlePreview}
            onChange={handleChange}
            onRemove={removeImage}
            beforeUpload={validateImageType}
            data-test-id="upload-image-button"
            multiple
            disabled={!canUser(ProjectUserPermissions.EditProjectImages)}
            itemRender={(originNode, file) => (
              <DefaultImageSelector originNode={originNode} file={file} />
            )}
          >
            {canUser(ProjectUserPermissions.EditProjectImages) && (
              <div>
                <FontAwesomeIcon icon={faPlus} />
                <div style={{ marginTop: 8 }}>
                  {intl.formatMessage({ id: "project.upload" })}
                </div>
              </div>
            )}
          </S.StyledUpload>
          <div style={{ display: "none" }}>
            <Image.PreviewGroup
              preview={{
                visible: preview.show,
                onVisibleChange: () => setPreview({ show: false, index: 0 }),
                current: preview.index,
              }}
            >
              {imageList.map((image) => (
                <Image
                  width={200}
                  src={image.url}
                  key={image.id || image.uid}
                />
              ))}
            </Image.PreviewGroup>
          </div>
        </>
      )}
    </GlobalS.GrayCard>
  );
};
